import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Clock, Mail, Trash2, Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export const ScheduleManager = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    scheduleName: '',
    auditType: 'technical_audit',
    frequency: 'weekly',
    dayOfWeek: '1',
    timeOfDay: '09:00',
    emailNotifications: true,
    notificationEmail: ''
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    const { data, error } = await supabase
      .from('seo_schedules')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSchedules(data);
    }
  };

  const handleCreateSchedule = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-scheduler', {
        body: {
          action: 'create_schedule',
          data: formData
        }
      });

      if (error) throw error;

      toast({
        title: 'Schedule Created',
        description: 'Your SEO audit schedule has been created successfully',
      });

      fetchSchedules();
      setFormData({
        scheduleName: '',
        auditType: 'technical_audit',
        frequency: 'weekly',
        dayOfWeek: '1',
        timeOfDay: '09:00',
        emailNotifications: true,
        notificationEmail: ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to create schedule',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async (scheduleId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-scheduler', {
        body: {
          action: 'run_scheduled_audit',
          data: { scheduleId }
        }
      });

      if (error) throw error;

      toast({
        title: 'Audit Started',
        description: 'The audit is running and you will receive an email when complete',
      });

      fetchSchedules();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to run audit',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scheduleId: string) => {
    const { error } = await supabase
      .from('seo_schedules')
      .delete()
      .eq('id', scheduleId);

    if (!error) {
      toast({
        title: 'Schedule Deleted',
        description: 'The schedule has been removed',
      });
      fetchSchedules();
    }
  };

  const handleToggleActive = async (scheduleId: string, isActive: boolean) => {
    const { error } = await supabase
      .from('seo_schedules')
      .update({ is_active: !isActive })
      .eq('id', scheduleId);

    if (!error) {
      toast({
        title: isActive ? 'Schedule Paused' : 'Schedule Activated',
        description: isActive ? 'The schedule has been paused' : 'The schedule is now active',
      });
      fetchSchedules();
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Create Scheduled Audit
          </CardTitle>
          <CardDescription>
            Set up automated SEO audits with email notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduleName">Schedule Name</Label>
              <Input
                id="scheduleName"
                placeholder="e.g., Weekly Technical Audit"
                value={formData.scheduleName}
                onChange={(e) => setFormData({ ...formData, scheduleName: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="auditType">Audit Type</Label>
              <Select value={formData.auditType} onValueChange={(value) => setFormData({ ...formData, auditType: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical_audit">Technical Audit</SelectItem>
                  <SelectItem value="backlink_audit">Backlink Audit</SelectItem>
                  <SelectItem value="content_audit">Content Audit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={formData.frequency} onValueChange={(value) => setFormData({ ...formData, frequency: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label htmlFor="dayOfWeek">Day of Week</Label>
                <Select value={formData.dayOfWeek} onValueChange={(value) => setFormData({ ...formData, dayOfWeek: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Sunday</SelectItem>
                    <SelectItem value="1">Monday</SelectItem>
                    <SelectItem value="2">Tuesday</SelectItem>
                    <SelectItem value="3">Wednesday</SelectItem>
                    <SelectItem value="4">Thursday</SelectItem>
                    <SelectItem value="5">Friday</SelectItem>
                    <SelectItem value="6">Saturday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="timeOfDay">Time of Day</Label>
              <Input
                id="timeOfDay"
                type="time"
                value={formData.timeOfDay}
                onChange={(e) => setFormData({ ...formData, timeOfDay: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notificationEmail">Notification Email</Label>
              <Input
                id="notificationEmail"
                type="email"
                placeholder="email@example.com"
                value={formData.notificationEmail}
                onChange={(e) => setFormData({ ...formData, notificationEmail: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="emailNotifications"
              checked={formData.emailNotifications}
              onCheckedChange={(checked) => setFormData({ ...formData, emailNotifications: checked })}
            />
            <Label htmlFor="emailNotifications" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Send email notifications
            </Label>
          </div>

          <Button onClick={handleCreateSchedule} disabled={loading || !formData.scheduleName} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
            Create Schedule
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Active Schedules</CardTitle>
          <CardDescription>Manage your automated SEO audit schedules</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {schedules.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No schedules created yet</p>
            ) : (
              schedules.map((schedule) => (
                <Card key={schedule.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{schedule.schedule_name}</h3>
                          <Badge variant={schedule.is_active ? "default" : "secondary"}>
                            {schedule.is_active ? "Active" : "Paused"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {schedule.audit_type.replace('_', ' ')} • {schedule.frequency}
                        </p>
                        {schedule.next_run_at && (
                          <p className="text-xs text-muted-foreground">
                            Next run: {new Date(schedule.next_run_at).toLocaleString()}
                          </p>
                        )}
                        {schedule.last_run_at && (
                          <p className="text-xs text-muted-foreground">
                            Last run: {new Date(schedule.last_run_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleRunNow(schedule.id)} disabled={loading}>
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleToggleActive(schedule.id, schedule.is_active)}>
                          {schedule.is_active ? "Pause" : "Resume"}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(schedule.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
